import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Trash2, CopyCheck, FlaskConical, ShieldCheck, AlertTriangle } from "lucide-react";
import { formatFechaCorta } from "@/lib/agenda";

const Fila = ({ r, tipo }) => (
  <div
    className={`flex flex-wrap items-center gap-2 text-sm rounded px-2 py-1 ${
      tipo === "conservar" ? "bg-emerald-50 text-emerald-900" : "bg-red-50 text-red-900"
    }`}
    data-testid={`dup-${tipo}-${r.codigo_booking}`}
  >
    <span className="text-[11px] font-semibold uppercase tracking-wide">
      {tipo === "conservar" ? "conservar" : "borrar"}
    </span>
    <span className="font-mono text-xs">{r.codigo_booking}</span>
    <span>{r.pasajero}</span>
    <span className="text-slate-500">
      {r.tour || "sin tour"} · {r.fecha ? formatFechaCorta(r.fecha) : "sin fecha"} {r.hora}
    </span>
    <span className="text-slate-500 capitalize">{r.estado}</span>
    <span className="text-slate-500">{r.salida_id ? "con salida" : "sin salida"}</span>
    <span className="text-slate-400">{(r.canales || []).join(", ")}</span>
    {r.motivo && <span className="text-[11px] italic text-slate-600">→ {r.motivo}</span>}
  </div>
);

export function LimpiezaDatosDialog({ open, onOpenChange, onDone }) {
  const [tab, setTab] = useState("duplicados");
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [prueba, setPrueba] = useState([]);
  const [seleccion, setSeleccion] = useState({});
  const [aplicando, setAplicando] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dup, pru] = await Promise.all([
        api.get("/reservas/duplicados"),
        api.get("/reservas/registros-prueba"),
      ]);
      setPlan(dup.data);
      setPrueba(pru.data.detalle || []);
      const pre = {};
      (dup.data.grupos || []).forEach((g) => { if (g.auto_aplicable) pre[g.conservar.id] = true; });
      setSeleccion(pre);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const grupos = plan?.grupos || [];
  const ids = Object.keys(seleccion).filter((k) => seleccion[k]);
  const aBorrar = grupos.filter((g) => seleccion[g.conservar.id]).reduce((n, g) => n + g.borrar.length, 0);

  const aplicar = async () => {
    setAplicando(true);
    try {
      const { data } = await api.post("/reservas/duplicados/aplicar", { ids });
      toast.success(`${data.eliminadas} duplicada(s) eliminada(s) · ${data.salidas_recalculadas.length} salida(s) con cupo recalculado.`);
      await load();
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAplicando(false);
    }
  };

  const eliminarPrueba = async () => {
    setAplicando(true);
    try {
      const { data } = await api.post("/reservas/registros-prueba/eliminar");
      toast.success(`${data.eliminadas} registro(s) de prueba eliminado(s).`);
      await load();
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAplicando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="limpieza-datos-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Revisar duplicados y datos de prueba</DialogTitle>
          <DialogDescription>
            Vista previa: nada se borra hasta que lo confirmes. Se conserva la reserva con salida
            asignada y mejor estado; se descarta la que quedó con la fecha del día de ingesta.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="duplicados" data-testid="tab-duplicados" className="gap-1.5">
              <CopyCheck className="h-3.5 w-3.5" /> Duplicados ({grupos.length})
            </TabsTrigger>
            <TabsTrigger value="prueba" data-testid="tab-registros-prueba" className="gap-1.5">
              <FlaskConical className="h-3.5 w-3.5" /> Registros de prueba ({prueba.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="duplicados" className="space-y-3 pt-3">
            {loading ? (
              <div className="h-32 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
            ) : grupos.length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center" data-testid="duplicados-empty">
                No hay duplicados: ningún código se repite y ningún pasajero aparece dos veces en el mismo tour, fecha y hora.
              </p>
            ) : (
              <>
                <p className="text-xs text-slate-500" data-testid="duplicados-resumen">
                  {plan.por_familia.codigo} grupo(s) con el mismo código y {plan.por_familia.pasajero} con el
                  mismo pasajero+tour+fecha+hora · {plan.a_borrar} reserva(s) a borrar ·
                  {" "}{plan.requieren_revision_manual} necesitan tu revisión (no son idénticas).
                </p>
                {grupos.map((g, gi) => (
                  <div key={gi} className="rounded-md border border-slate-200 p-3 space-y-1.5" data-testid={`duplicado-grupo-${gi}`}>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
                      <Checkbox
                        checked={!!seleccion[g.conservar.id]}
                        onCheckedChange={(v) => setSeleccion((s) => ({ ...s, [g.conservar.id]: !!v }))}
                        data-testid={`duplicado-check-${g.conservar.codigo_booking}`}
                      />
                      {g.familia === "codigo" ? "Mismo código" : "Mismo pasajero, tour, fecha y hora"}
                      <span className="font-mono text-xs text-slate-500">{g.familia === "codigo" ? g.clave : ""}</span>
                      {g.auto_aplicable ? (
                        <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">
                          <ShieldCheck className="h-3 w-3" /> seguro
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">
                          <AlertTriangle className="h-3 w-3" /> revisar: no son idénticas
                        </span>
                      )}
                    </label>
                    <Fila r={g.conservar} tipo="conservar" />
                    {g.borrar.map((b) => <Fila key={b.id} r={b} tipo="borrar" />)}
                  </div>
                ))}
              </>
            )}
          </TabsContent>

          <TabsContent value="prueba" className="pt-3">
            {loading ? (
              <div className="h-32 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
            ) : prueba.length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center" data-testid="registros-prueba-empty">
                No quedan registros de prueba en la base.
              </p>
            ) : (
              <div className="space-y-1.5">
                {prueba.map((r) => (
                  <div key={r.id} className="flex items-center gap-2 text-sm text-slate-600" data-testid={`registro-prueba-${r.codigo_booking}`}>
                    <span className="font-mono text-xs">{r.codigo_booking}</span>
                    <span className="text-slate-800">{r.pasajero}</span>
                    <span className="text-slate-400">{r.tour} · {r.fecha ? formatFechaCorta(r.fecha) : "sin fecha"} {r.hora}</span>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
          {tab === "duplicados" ? (
            <Button
              onClick={aplicar}
              disabled={aplicando || aBorrar === 0}
              className="gap-1.5"
              data-testid="eliminar-duplicados-seleccion"
            >
              {aplicando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Borrar {aBorrar} duplicada(s)
            </Button>
          ) : (
            <Button
              onClick={eliminarPrueba}
              disabled={aplicando || prueba.length === 0}
              className="gap-1.5"
              data-testid="eliminar-registros-prueba"
            >
              {aplicando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Eliminar {prueba.length} registro(s) de prueba
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
